import React, { useCallback, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import FacilityAttestationForm from "features/careverify/pages/FacilityAttestationPage";
import { useFacility } from "hooks/useFacility";

/**
 * Facility Ops — CareVerify Manager product-line attestation (PROMPT 6).
 * Wraps the checklist form and persists via upsertBillingEntity callable.
 */
const FacilityAttestationDashboardPage = ({ data: dataProp }) => {
  const { data: facilityData } = useFacility();
  const data = dataProp?.id ? dataProp : facilityData;
  const facilityId = data?.id;
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const handleSubmit = useCallback(
    async ({ acceptedPayerPlans }) => {
      setError("");
      setOk("");
      if (!facilityId) {
        throw new Error("No facility selected.");
      }
      const fn = httpsCallable(getFunctions(), "upsertBillingEntity");
      await fn({
        facilityId,
        entityId: "facility-main",
        displayName: data?.name || data?.title || "Facility",
        entityType: "facility",
        role: "facility_fee",
        acceptedPayerPlans,
        active: true,
      });
      setOk("Attestation saved.");
    },
    [facilityId, data?.name, data?.title],
  );

  if (!facilityId) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 800 }}>
      <DashboardPageHeader
        title="CareVerify attestation"
        subtitle="Mark which product lines this facility accepts. Requires CareVerify Manager role."
      />
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {ok ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk("")}>
          {ok}
        </Alert>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Facility: {data?.name || data?.title || facilityId}
      </Typography>
      <FacilityAttestationForm
        onSubmitAttestation={async (payload) => {
          try {
            await handleSubmit(payload);
          } catch (err) {
            setError(err?.message || "Failed to save attestation.");
            throw err;
          }
        }}
      />
    </Box>
  );
};

export default FacilityAttestationDashboardPage;
